import { ReactNode } from 'react';

import { cx } from '@libs/classnames';
import { ErrorIcon } from '@ui/icons/ErrorIcon';
import { FeatherIcon } from '@ui/icons/FeatherIcon';

import { CAMERA_HINTS } from '@features/video/constants';
import { MediaDevices } from '@features/video/hooks/useMediaDevices';
import { MicIssue, RoomStatus } from '@features/video/types';
import { MicIssueBanner } from '@features/video/ui/MicIssueBanner';
import { StreamControls } from '@features/video/ui/StreamControls';
import { StreamSetup, StreamSetupProps } from '@features/video/ui/StreamSetup';
import { StreamStatus } from '@features/video/ui/StreamStatus';

import styles from './VideoStreamPage.module.css';

// Режим страницы: какую камеру участника снимает это устройство (подсказки те же, что в модалке)
const MODES: Record<VideoStreamMode, { title: string; hint: string }> = {
  main: { title: 'Основная камера', hint: CAMERA_HINTS[0]! },
  extra: { title: 'Дополнительная камера', hint: CAMERA_HINTS[1]! },
};

const HINTS: Record<RoomStatus, string> = {
  connecting: 'Подключаемся к комнате',
  reconnecting: 'Связь прервалась, пробуем восстановить',
  waiting: 'Судья проверяет трансляцию и допустит вас к игре',
  live: 'Не закрывайте страницу, пока не сыграны все партии',
};

// Дополнительная камера молчит, чтобы не было эха; звук включают, если на основной его нет
const SPEAKER_OFF_HINT =
  'Голос судьи звучит на основной камере. Если его не слышно, включите звук здесь.';

export type VideoStreamMode = 'main' | 'extra';

// 'unavailable' — видеоконтроль закрыт: турнир без видео или окончен, участник забанен
export type VideoStreamPageState = 'invalid' | 'unavailable' | 'setup' | RoomStatus | 'ended';

export type Props = {
  className?: string;
  mode: VideoStreamMode;
  state: VideoStreamPageState;
  tournamentTitle?: string;
  playerName?: string;
  roomNumber?: number;
  setup?: Omit<StreamSetupProps, 'className'>;
  // устройства в эфире: те же настройки, что до подключения, смена уходит в эфир сама
  devices?: MediaDevices;
  meterTrack?: MediaStreamTrack | null;
  micOn?: boolean;
  camOn?: boolean;
  speakerOn?: boolean;
  onToggleMic?: () => void;
  onToggleCam?: () => void;
  onToggleSpeaker?: () => void;
  onHangUp?: () => void;
  micIssue?: MicIssue;
  audioBlocked?: boolean;
  onFixMic?: () => void;
};

export function VideoStreamPage({
  className,
  mode,
  state,
  tournamentTitle,
  playerName,
  roomNumber,
  setup,
  devices,
  meterTrack,
  micOn = false,
  camOn = false,
  speakerOn = false,
  onToggleMic,
  onToggleCam,
  onToggleSpeaker,
  onHangUp,
  micIssue,
  audioBlocked,
  onFixMic,
}: Props) {
  const { title, hint } = MODES[mode];
  const roomStatus = isRoomStatus(state) ? state : null;
  const isBusy = roomStatus === 'connecting' || roomStatus === 'reconnecting';
  const isSpeakerMuted = !!onToggleSpeaker && !speakerOn;
  const subtitle = [roomNumber ? `Комната ${roomNumber}` : null, playerName, tournamentTitle]
    .filter(Boolean)
    .join(' · ');
  const hintNode = (
    <>
      <b>В кадре должно быть видно:</b> {hint}
    </>
  );

  return (
    <div className={cx(styles.page, className)}>
      <header className={styles.page__header}>
        <FeatherIcon className={styles.page__logo} />
        <div className={styles.page__titles}>
          <h1 className={styles.page__title}>{title}</h1>
          {subtitle && <p className={styles.page__subtitle}>{subtitle}</p>}
        </div>
      </header>

      {state === 'invalid' && (
        <Notice
          icon={<ErrorIcon />}
          title="Ссылка недействительна"
          text="Ссылка устарела или уже использована. Откройте страницу турнира на компьютере и получите новую."
        />
      )}

      {state === 'unavailable' && (
        <Notice
          icon={<ErrorIcon />}
          title="Видеоконтроль недоступен"
          text="Трансляция для этого турнира закрыта. Если это ошибка, обратитесь к судье."
        />
      )}

      {state === 'setup' && setup && (
        <StreamSetup {...setup} hint={hintNode} joinLabel="Начать трансляцию" />
      )}

      {/* в эфире те же настройки: смена камеры или микрофона уходит в комнату сама */}
      {roomStatus && devices && (
        <StreamSetup
          devices={devices}
          meterTrack={meterTrack}
          hint={hintNode}
          live={
            <>
              <div className={styles.page__status}>
                <StreamStatus status={roomStatus} />
              </div>
              <StreamControls
                micOn={micOn}
                camOn={camOn}
                speakerOn={speakerOn}
                disabled={isBusy}
                onToggleMic={onToggleMic}
                onToggleCam={onToggleCam}
                onToggleSpeaker={onToggleSpeaker}
                onHangUp={onHangUp}
              />
              <p className={styles.page__hint}>
                {roomStatus === 'live' && isSpeakerMuted ? SPEAKER_OFF_HINT : HINTS[roomStatus]}
              </p>
            </>
          }
        />
      )}
      {/* та же плашка, что у судьи и на ПК: поверх страницы */}
      {roomStatus && onFixMic && (
        <MicIssueBanner issue={micIssue} audioBlocked={audioBlocked} onFix={onFixMic} />
      )}

      {state === 'ended' && (
        <Notice
          title="Трансляция завершена"
          text="Все партии сыграны, видеоконтроль больше не нужен. Страницу можно закрыть."
        />
      )}
    </div>
  );
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

function isRoomStatus(state: VideoStreamPageState): state is RoomStatus {
  return state !== 'invalid' && state !== 'unavailable' && state !== 'setup' && state !== 'ended';
}

function Notice({
  icon,
  title,
  text,
  action,
}: {
  icon?: ReactNode;
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <div className={styles.notice}>
      {icon && <div className={styles.notice__icon}>{icon}</div>}
      <h2 className={styles.notice__title}>{title}</h2>
      <p className={styles.notice__text}>{text}</p>
      {action}
    </div>
  );
}
